import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import ProjectPreview from './components/ProjectPreview';
import VendorRegistrationForm from './components/VendorRegistrationForm';
import PlatformBenefits from './components/PlatformBenefits';
import TrustIndicators from './components/TrustIndicators';
import Icon from '../../components/AppIcon';
import { projectService } from '../../services/projectService';

const VendorQuoteRequestLanding = () => {
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const projectId = searchParams?.get('projectId');
  const vendorName = searchParams?.get('vendorName');

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setError('No project ID provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await projectService?.getByIdPublic(projectId);
        setProjectData(data);
      } catch (err) {
        setError('Unable to load project details. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleRegistrationSuccess = (vendorInfo) => {
    setIsRegistrationOpen(false);
    // Navigate to vendor project details with vendorId and projectId as query params
    navigate(`/vendor-project-details?projectId=${projectData?.id}&vendorId=${vendorInfo?.vendorId}&vendorName=${encodeURIComponent(vendorInfo?.businessName || '')}&vendorEmail=${encodeURIComponent(vendorInfo?.email || '')}`);
  };

  const handleJoinPlatform = () => {
    setIsRegistrationOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-primary/5 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Icon name="Mail" size={16} className="mr-2" />
                {vendorName ? `Hey ${vendorName}! Someone's Requested a Quote!` : "Someone's Requested a Quote!"}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Join Stackd Vendor Base
              </h1>
              <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                Access premium quote opportunities and grow your business with our trusted platform
              </p>
            </div>

            {/* Main CTA */}
            <div className="text-center mb-12">
              <Button
                variant="default"
                size="xl"
                onClick={handleJoinPlatform}
                iconName="UserPlus"
                iconPosition="left"
                className="mb-4"
              >
                Join Stackd Vendor Base to View Full Details & Submit Quote
              </Button>
              <p className="text-base font-medium text-text-secondary">
                Free to join • Instant access • No monthly fees
              </p>
              <p className="text-base font-medium text-text-secondary mt-1">
                Only pay on quotes you win
              </p>
            </div>
          </div>
        </section>

        {/* Project Preview Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Project Preview
              </h2>
              <p className="text-text-secondary">
                See what's waiting for you after registration
              </p>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                <span className="ml-3 text-text-secondary">Loading project details...</span>
              </div>
            )}

            {error && !loading && (
              <div className="max-w-2xl mx-auto text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
                  <Icon name="AlertCircle" size={28} className="text-destructive" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Project Not Found</h3>
                <p className="text-text-secondary">{error}</p>
              </div>
            )}

            {!loading && !error && projectData && (
              <ProjectPreview 
                project={projectData} 
                onRegisterClick={handleJoinPlatform}
              />
            )}
          </div>
        </section>

        {/* Platform Benefits */}
        <PlatformBenefits />

        {/* Trust Indicators */}
        <TrustIndicators />

        {/* Final CTA Section */}
        <section className="py-16 bg-muted/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Access This Opportunity?
            </h2>
            <p className="text-xl text-text-secondary mb-8">
              Join thousands of successful vendors already using Stackd to grow their business
            </p>
            <Button
              variant="default"
              size="xl"
              onClick={handleJoinPlatform}
              iconName="ArrowRight"
              iconPosition="right"
              className="mb-4"
            >
              Get Started - It's Free
            </Button>
            <p className="text-sm text-text-secondary">
              Registration takes less than 2 minutes
            </p>
          </div>
        </section>

        {/* Registration Modal */}
        {isRegistrationOpen && (
          <VendorRegistrationForm
            isOpen={isRegistrationOpen}
            onClose={() => setIsRegistrationOpen(false)}
            onSuccess={handleRegistrationSuccess}
            projectId={projectData?.id}
          />
        )}
      </main>
    </div>
  );
};

export default VendorQuoteRequestLanding;